import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useFriends } from '../context/FriendContext';
import { useCruises } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import CalendarOverlay from '../components/CalendarOverlay';
import { PublicCruiseView } from '../types/friends';
import { ACCESS_LEVEL_LABELS } from '../types/friends';
import { FriendCruisesSkeleton } from '../components/LoadingSkeleton';
import {
  ChevronLeft,
  Calendar,
  List,
  Ship,
  MapPin,
  Clock,
  Users,
  Calendar as CalendarIcon,
  Waves,
  Compass,
  PartyPopper,
  Navigation,
} from 'lucide-react';
import { parseISO, startOfDay, isSameDay, differenceInDays } from 'date-fns';
import { isCruiseCurrentlySailing, getCruiseEndTime } from '../utils/dateUtils';
import { formatFriendDisplayName } from '../utils/friendDisplayUtils';
import { motion } from 'framer-motion';
import LazyImage from '../components/LazyImage';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

type ViewMode = 'list' | 'calendar';

const FriendCruises = () => {
  const { friendId } = useParams<{ friendId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { friends, getFriendCruises, loading: friendsLoading } = useFriends();
  const { cruises: userCruises } = useCruises();
  const { getShipImageUrl, getShipLocationUrl, getShipDeckplanUrl } = useCruiseMetadata();
  const { formatDate, formatTimeString } = useDisplayPreferences();

  const [friendCruises, setFriendCruises] = useState<PublicCruiseView[]>([]);
  const [loading, setLoading] = useState(true);
  const viewMode = (searchParams.get('tab') || 'list') as ViewMode;
  const [showUserCalendar, setShowUserCalendar] = useState(true);
  const [showFriendCalendar, setShowFriendCalendar] = useState(true);

  const setViewMode = (mode: ViewMode) => {
    setSearchParams({ tab: mode });
  };

  // Helper function to check if a cruise is currently sailing
  const isCurrentlySailing = (cruise: PublicCruiseView): boolean => {
    return isCruiseCurrentlySailing(cruise);
  };

  // Helper function to get current day of cruise
  const getCurrentCruiseDay = (cruise: PublicCruiseView): number => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return differenceInDays(today, departureDate) + 1;
  };

  // Helper function to get total days of cruise
  const getTotalCruiseDays = (cruise: PublicCruiseView): number => {
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return differenceInDays(returnDate, departureDate);
  };

  // Helper function to check if today is departure day
  const isDepartureDay = (cruise: PublicCruiseView): boolean => {
    const today = startOfDay(new Date());
    const departureDate = startOfDay(parseISO(cruise.departureDate));
    return isSameDay(today, departureDate);
  };

  // Helper function to check if today is return day (disembarkation day)
  const isReturnDay = (cruise: PublicCruiseView): boolean => {
    const today = startOfDay(new Date());
    const returnDate = startOfDay(parseISO(cruise.returnDate));
    return isSameDay(today, returnDate);
  };

  // Helper function to get today's port
  const getTodaysPort = (cruise: PublicCruiseView) => {
    const today = startOfDay(new Date());
    return cruise.ports?.find(port => {
      if (!port.date) return false;
      const portDate = startOfDay(parseISO(port.date));
      return isSameDay(portDate, today);
    });
  };

  // Helper function to get today's location info
  const getTodaysLocation = (cruise: PublicCruiseView) => {
    if (isDepartureDay(cruise)) {
      return {
        icon: PartyPopper,
        iconColor: 'text-green-500',
        title: 'Boarding Day!',
        subtitle: 'Their cruise adventure begins today',
      };
    }

    const todaysPort = getTodaysPort(cruise);
    if (todaysPort) {
      return {
        icon: Compass,
        iconColor: 'text-blue-500',
        title: todaysPort.name,
        subtitle: formatDate(parseISO(todaysPort.date), { includeWeekday: true, shortFormat: true }),
        arrivalTime: todaysPort.arrivalTime,
        departureTime: todaysPort.departureTime,
      };
    }

    return {
      icon: Waves,
      iconColor: 'text-blue-400',
      title: 'Sea Day',
      subtitle: 'At sea today',
    };
  };

  // Separate currently sailing and upcoming cruises
  const currentlySailingCruises = useMemo(() => {
    return friendCruises.filter(cruise => isCurrentlySailing(cruise));
  }, [friendCruises]);

  const upcomingFriendCruises = useMemo(() => {
    const now = new Date();
    return friendCruises
      .filter(cruise => {
        // Not currently sailing, but end time is still in the future
        return !isCurrentlySailing(cruise) && getCruiseEndTime(cruise) > now;
      })
      .sort((a, b) => {
        const dateA = parseISO(a.departureDate).getTime();
        const dateB = parseISO(b.departureDate).getTime();
        return dateA - dateB; // Ascending order (next future first)
      });
  }, [friendCruises]);

  // State to manage skeleton display timing
  const [dataLoaded, setDataLoaded] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  const friend = friends.find((f) => f.friendId === friendId);

  // Track when data is actually loaded
  useEffect(() => {
    if (!friendsLoading && !loading && friend) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [friendsLoading, loading, friend]);

  // Ensure skeleton shows for minimum duration
  useEffect(() => {
    if (dataLoaded) {
      const timer = setTimeout(() => {
        setMinTimeElapsed(true);
      }, 500); // 500ms minimum skeleton display
      return () => clearTimeout(timer);
    } else {
      setMinTimeElapsed(false);
    }
  }, [dataLoaded]);

  useEffect(() => {
    if (!friendId) {
      navigate('/friends');
      return;
    }

    const loadFriendCruises = async () => {
      try {
        setLoading(true);
        const cruises = await getFriendCruises(friendId);
        setFriendCruises(cruises);
      } catch (error) {
        console.error('Error loading friend cruises:', error);
      } finally {
        setLoading(false);
      }
    };

    if (friend) {
      loadFriendCruises();
    }
  }, [friendId, friend, getFriendCruises]);

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/friends');
    }
  };

  // Show skeleton until both data is loaded AND minimum time has elapsed
  if (!dataLoaded || !minTimeElapsed) {
    return <FriendCruisesSkeleton />;
  }

  if (!friend) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">Friend not found</p>
          <button
            onClick={handleBackNavigation}
            className="inline-flex items-center gap-1 text-secondary-600 hover:text-secondary-500 font-medium"
          >
            <ChevronLeft className="h-5 w-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center">
          <button
            onClick={handleBackNavigation}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Users className="h-8 w-8 text-secondary-600 shrink-0" />
              {formatFriendDisplayName(friend)}&apos;s Cruises
            </h1>
            <p className="text-gray-600 mt-1">
              Access Level: <span className="font-semibold">{ACCESS_LEVEL_LABELS[friend.accessLevel]}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                viewMode === 'list'
                  ? 'border-secondary-500 text-secondary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="h-5 w-5" />
              Cruise List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                viewMode === 'calendar'
                  ? 'border-secondary-500 text-secondary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="h-5 w-5" />
              Calendar
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {viewMode === 'calendar' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Calendar Comparison</h2>
              <CalendarOverlay
                userCruises={userCruises}
                friendCruises={[...currentlySailingCruises, ...upcomingFriendCruises]}
                showUserCalendar={showUserCalendar}
                showFriendCalendar={showFriendCalendar}
                onShowUserCalendarChange={setShowUserCalendar}
                onShowFriendCalendarChange={setShowFriendCalendar}
              />
            </motion.div>
          ) : (
            <div className="space-y-4">
              {/* Currently Sailing Section */}
              {currentlySailingCruises.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Waves className="h-5 w-5 text-secondary-500" />
                    Currently Sailing
                  </h2>
                  {currentlySailingCruises.map((cruise) => {
                    const shipImageUrl = cruise.imageUrl || getShipImageUrl(cruise.cruiseLine, cruise.shipName);
                    const isSailing = isCurrentlySailing(cruise);
                    const currentDay = getCurrentCruiseDay(cruise);
                    const totalDays = getTotalCruiseDays(cruise);
                    const todaysLocation = getTodaysLocation(cruise);

                    return (
                      <motion.div
                        key={cruise.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-md overflow-hidden border-2 border-blue-300 hover:shadow-lg transition-shadow duration-300"
                      >
                        {/* Currently Sailing Badge */}
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Waves className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm font-semibold min-w-0 break-words">
                              {(friend.friendUsername ?? friend.friendDisplayName ?? friend.friendEmail)} is currently sailing
                            </span>
                          </div>
                          <span className="text-xs bg-white/20 px-2 py-1 rounded-full flex-shrink-0 whitespace-nowrap min-w-[5rem] text-center">
                            {isReturnDay(cruise) ? 'Disembarkation Day' : `Day ${currentDay} of ${totalDays}`}
                          </span>
                        </div>

                        {/* Content: Image and Details - Responsive Layout */}
                        <div className="flex flex-col lg:flex-row lg:items-stretch">
                          {/* Ship Image - Left Column on Large Screens */}
                          <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 relative overflow-hidden lg:flex">
                            {shipImageUrl ? (
                              <>
                                <LazyImage
                                  src={shipImageUrl}
                                  alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                                  className="w-full h-full object-cover lg:absolute lg:inset-0"
                                />
                                <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
                              </>
                            ) : (
                              <>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-600"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <CustomCruiseIcon size={136} className="text-white/30" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-black/60 to-transparent"></div>
                              </>
                            )}
                            
                            {/* Ship Info Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                              <h3 className="text-xl font-semibold truncate">{cruise.shipName}</h3>
                              <p className="text-sm opacity-90">{cruise.cruiseLine}</p>
                            </div>
                          </div>

                          {/* Cruise Details - Right Column on Large Screens */}
                          <div className="p-4 flex-1">
                            {/* Today's Location */}
                            {isSailing && (
                              <div className="mb-4 p-3 bg-white rounded-lg shadow-sm">
                                <div className="flex items-start gap-2">
                                  <todaysLocation.icon className={`h-5 w-5 ${todaysLocation.iconColor} mt-0.5 flex-shrink-0`} />
                                  <div className="flex-1">
                                    <p className={`text-sm font-semibold ${todaysLocation.iconColor}`}>
                                      {todaysLocation.title}
                                    </p>
                                    <p className="text-xs text-gray-600">{todaysLocation.subtitle}</p>
                                    {todaysLocation.arrivalTime && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Arrival: {formatTimeString(todaysLocation.arrivalTime)}
                                      </p>
                                    )}
                                    {todaysLocation.departureTime && (
                                      <p className="text-xs text-gray-500">
                                        Departure: {formatTimeString(todaysLocation.departureTime)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Date and Port Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div className="flex items-start space-x-2">
                                <CalendarIcon className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {formatDate(parseISO(cruise.departureDate))}
                                  </p>
                                  <p className="text-xs text-gray-500">Departure</p>
                                </div>
                              </div>
                              <div className="flex items-start space-x-2">
                                <MapPin className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {cruise.departurePort}
                                  </p>
                                  <p className="text-xs text-gray-500">Departure Port</p>
                                </div>
                              </div>
                            </div>

                            {/* Date Range */}
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                              <Clock className="h-4 w-4" />
                              <span>
                                {formatDate(parseISO(cruise.departureDate), { shortFormat: true })} -{' '}
                                {formatDate(parseISO(cruise.returnDate))}
                              </span>
                            </div>

                            {/* Ports of Call - blue theme for currently sailing card */}
                            {cruise.ports && cruise.ports.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-700 mb-2">Ports of Call:</p>
                                <div className="flex flex-wrap gap-2">
                                  {cruise.ports
                                    .filter(port => port.name?.trim().toLowerCase() !== cruise.departurePort?.trim().toLowerCase())
                                    .map((port) => (
                                    <span
                                      key={port.id}
                                      className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full"
                                    >
                                      {port.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Ship Details / Track Ship Location / Deck Plan */}
                            <div className="flex flex-wrap gap-2 mt-4">
                              <Link
                                to={`/ship/${encodeURIComponent(cruise.cruiseLine)}/${encodeURIComponent(cruise.shipName)}`}
                                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                              >
                                <Ship className="h-4 w-4 mr-2" />
                                Ship Details
                              </Link>
                              {(() => {
                                const shipLocationUrl = getShipLocationUrl(cruise.cruiseLine, cruise.shipName);
                                const shipDeckplanUrl = getShipDeckplanUrl(cruise.cruiseLine, cruise.shipName);
                                return (
                                  <>
                                    {shipLocationUrl && (
                                      <a
                                        href={shipLocationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                                      >
                                        <Navigation className="h-4 w-4 mr-2" />
                                        Track Ship Location
                                      </a>
                                    )}
                                    {shipDeckplanUrl && (
                                      <a
                                        href={shipDeckplanUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-500 transition-colors font-medium"
                                      >
                                        <MapPin className="h-4 w-4 mr-2" />
                                        View Deck Plan
                                      </a>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Upcoming Cruises Section */}
              {upcomingFriendCruises.length === 0 && currentlySailingCruises.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <Ship className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg text-gray-600 mb-2">No upcoming cruises</p>
                  <p className="text-sm text-gray-500">
                    {friend.accessLevel === 'restricted-access'
                      ? 'This friend has not shared any upcoming cruises with you yet.'
                      : 'This friend has no upcoming cruises in their calendar.'}
                  </p>
                </div>
              ) : (
                <>
                  {currentlySailingCruises.length > 0 && (
                    <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mt-6">
                      <CalendarIcon className="h-5 w-5 text-secondary-500" />
                      Upcoming Cruises
                    </h2>
                  )}
                  {upcomingFriendCruises.map((cruise) => {
                  const shipImageUrl = cruise.imageUrl || getShipImageUrl(cruise.cruiseLine, cruise.shipName);
                  return (
                  <motion.div
                    key={cruise.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    {/* Content: Image and Details - Responsive Layout */}
                    <div className="flex flex-col lg:flex-row lg:items-stretch">
                      {/* Ship Image - Left Column on Large Screens */}
                      <div className="h-40 lg:h-auto lg:w-[30rem] xl:w-[34rem] lg:flex-shrink-0 relative overflow-hidden lg:flex">
                        {shipImageUrl ? (
                          <>
                            <LazyImage
                              src={shipImageUrl}
                              alt={`${cruise.shipName} - ${cruise.cruiseLine}`}
                              className="w-full h-full object-cover lg:absolute lg:inset-0"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                          </>
                        ) : (
                          <>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-600"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <CustomCruiseIcon size={136} className="text-white/30" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                          </>
                        )}
                        
                        {/* Ship Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                          <h3 className="text-xl font-semibold truncate">{cruise.shipName}</h3>
                          <p className="text-sm opacity-90">{cruise.cruiseLine}</p>
                        </div>
                      </div>

                      {/* Cruise Details - Right Column on Large Screens */}
                      <div className="p-4 flex-1">
                        {/* Date and Port Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-start space-x-2">
                            <CalendarIcon className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {formatDate(parseISO(cruise.departureDate))}
                              </p>
                              <p className="text-xs text-gray-500">Departure</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-2">
                            <MapPin className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {cruise.departurePort}
                              </p>
                              <p className="text-xs text-gray-500">Departure Port</p>
                            </div>
                          </div>
                        </div>

                        {/* Date Range */}
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                          <Clock className="h-4 w-4" />
                          <span>
                            {formatDate(parseISO(cruise.departureDate), { shortFormat: true })} -{' '}
                            {formatDate(parseISO(cruise.returnDate))}
                          </span>
                        </div>

                        {/* Ports of Call */}
                        {cruise.ports && cruise.ports.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-medium text-gray-700 mb-2">Ports of Call:</p>
                            <div className="flex flex-wrap gap-2">
                              {cruise.ports
                                .filter(port => port.name?.trim().toLowerCase() !== cruise.departurePort?.trim().toLowerCase())
                                .map((port) => (
                                <span
                                  key={port.id}
                                  className="text-xs bg-secondary-100 text-secondary-800 px-2 py-1 rounded-full"
                                >
                                  {port.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Ship Details / Track Ship Location / Deck Plan (Upcoming) */}
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Link
                            to={`/ship/${encodeURIComponent(cruise.cruiseLine)}/${encodeURIComponent(cruise.shipName)}`}
                            className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                          >
                            <Ship className="h-4 w-4 mr-2" />
                            Ship Details
                          </Link>
                          {(() => {
                            const shipLocationUrl = getShipLocationUrl(cruise.cruiseLine, cruise.shipName);
                            const shipDeckplanUrl = getShipDeckplanUrl(cruise.cruiseLine, cruise.shipName);
                            return (
                              <>
                                {shipLocationUrl && (
                                  <a
                                    href={shipLocationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                                  >
                                    <Navigation className="h-4 w-4 mr-2" />
                                    Track Ship Location
                                  </a>
                                )}
                                {shipDeckplanUrl && (
                                  <a
                                    href={shipDeckplanUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition-colors font-medium"
                                  >
                                    <MapPin className="h-4 w-4 mr-2" />
                                    View Deck Plan
                                  </a>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {/* Ratings */}
                        {cruise.ratings && (
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-2">Ratings:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-gray-600">
                              {cruise.ratings.shipExperience && (
                                <div>
                                  Ship: {cruise.ratings.shipExperience}/10
                                </div>
                              )}
                              {cruise.ratings.service && (
                                <div>Service: {cruise.ratings.service}/10</div>
                              )}
                              {cruise.ratings.foodDining && (
                                <div>Food: {cruise.ratings.foodDining}/10</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FriendCruises;

